import * as babylon from "@babylonjs/core";

import * as schema from "@/shared/state/visualization/schema/v1/three-d";

/**
 * One sample's worth of channel values, as the rotation they describe.
 *
 * Every mode collapses to a quaternion here, once, at load time — so the
 * per-frame path only ever slerps, and the hierarchy composes one kind of thing.
 *
 * Degenerate input yields no rotation rather than `NaN`: a zero axis names no
 * direction, and an all-zero quaternion is what a channel with no data at this
 * time produces. Both would otherwise poison every descendant's transform, since
 * a `NaN` propagates through the whole composition.
 *
 * @param spec the rotation being sampled, which fixes how `values` are read
 * @param values `[angle]`, `[roll, pitch, yaw]`, or `[x, y, z, w]` — a bare
 *   number is accepted for the single-channel case
 */
export function quaternionFor(
  spec: schema.Rotation,
  values: number[] | number,
): babylon.Quaternion {
  const channels = typeof values === "number" ? [values] : values;

  switch (spec.type) {
    case "axis_angle": {
      const axis = new babylon.Vector3(
        spec.axis[0],
        spec.axis[1],
        spec.axis[2],
      );
      if (axis.lengthSquared() === 0) {
        return babylon.Quaternion.Identity();
      }
      return babylon.Quaternion.RotationAxis(
        axis.normalize(),
        channels[0] ?? 0,
      );
    }
    case "euler": {
      // Babylon's own argument order is (yaw about Y, pitch about X, roll about
      // Z), which names the axes differently from URDF. Built from the axes
      // directly so the mapping is stated rather than assumed: roll turns about
      // X, pitch about Y, yaw about Z.
      const [roll = 0, pitch = 0, yaw = 0] = channels;
      return babylon.Quaternion.RotationYawPitchRoll(pitch, roll, yaw);
    }
    case "quaternion": {
      const [x = 0, y = 0, z = 0, w = 0] = channels;
      const quaternion = new babylon.Quaternion(x, y, z, w);
      if (quaternion.lengthSquared() === 0) {
        return babylon.Quaternion.Identity();
      }
      return quaternion.normalize();
    }
  }
}
